import React, { memo } from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Add from '@mui/icons-material/Add';
import Check from '@mui/icons-material/Check';
import { alpha } from '@mui/material/styles';

interface ToggleChipProps {
  label: string;
  /** Muted secondary text after the label. */
  description?: string;
  selected: boolean;
  onClick: () => void;
  tone?: 'primary' | 'error';
  /** Icon shown while selected; defaults to a check mark. */
  selectedIcon?: React.ReactNode;
  size?: 'small' | 'medium' | 'large';
  disabled?: boolean;
}

const HEIGHT = { small: 32, medium: 36, large: 44 } as const;

/** Outlined on/off button with add/check affordance (aria-pressed). */
const ToggleChip: React.FC<ToggleChipProps> = ({ label, description, selected, onClick, tone = 'primary', selectedIcon, size = 'medium', disabled }) => (
  <ButtonBase
    aria-pressed={selected}
    onClick={onClick}
    disabled={disabled}
    sx={(theme) => {
      const color = tone === 'error' ? theme.palette.error.main : theme.palette.primary.dark;
      return {
        height: HEIGHT[size],
        gap: 0.5,
        pl: size === 'small' ? 1 : 1.25,
        pr: size === 'small' ? 1.25 : 1.75,
        borderRadius: 2,
        border: 1,
        borderColor: selected ? color : 'divider',
        bgcolor: selected ? alpha(color, 0.1) : 'background.paper',
        color: selected ? color : 'text.secondary',
        fontSize: size === 'small' ? 12 : 13,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        transition: 'border-color 120ms, background-color 120ms',
        '& svg': { fontSize: size === 'small' ? 16 : 18 },
        '&:hover': { borderColor: color },
        '&.Mui-focusVisible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 1 },
        '&.Mui-disabled': { opacity: 0.7 },
      };
    }}
  >
    {selected ? selectedIcon ?? <Check /> : <Add />}
    {label}
    {description && (
      <Box component="span" sx={{ ml: 0.5, fontWeight: 400, fontSize: 12, color: 'text.secondary' }}>
        {description}
      </Box>
    )}
  </ButtonBase>
);

export default memo(ToggleChip);
